import React, { useEffect } from 'react';
import LoginForm from '../components/authform/loginForm'
import girsimage from '../pages/images/allLogo.png'

// HEX colors required: 1F6F5F 2FA084 6FCF97 EEEEEE
// If token exists, redirect to dashboard (auto-login user)
// Move redirect logic into an effect to avoid constant loading/render loop


function useRedirectIfAuthenticated() {
    useEffect(() => {
        const token = typeof window !== "undefined" ? localStorage.getItem('token') : null;
        if (token) {
            window.location.replace('/');
        }
    }, []);
}

function Login() {
    useRedirectIfAuthenticated();
    return (
        <div className="w-full min-h-screen flex items-stretch justify-center bg-gradient-to-br from-[#1F6F5F] via-[#2FA084] to-[#6FCF97] relative overflow-hidden">
            {/* Animated Aesthetic Blobs */}
            <div className="absolute top-0 left-1/2 bg-[#2FA084] opacity-40 w-[550px] h-[550px] rounded-full filter blur-3xl animate-pulse -z-10" style={{ transform: 'translateX(-65%) scaleX(1.2)' }} />
            <div className="absolute bottom-0 left-20 bg-[#6FCF97] opacity-30 w-[400px] h-[400px] rounded-full filter blur-2xl animate-blob2 -z-10" />
            <div className="absolute -top-20 right-24 bg-[#EEEEEE] opacity-50 w-[390px] h-[390px] rounded-full filter blur-2xl animate-blob3 -z-10" />
            
            <div className="flex flex-row w-full justify-center items-center relative z-10">
                {/* Left Content: Logo, Welcome, Taglines */}
                <div className="flex flex-col justify-between h-[480px] min-h-[400px] max-h-[550px] py-16 px-8 lg:px-14 w-[370px] max-w-[400px] bg-[#EEEEEE]/10 backdrop-blur-lg rounded-l-2xl border-t border-b border-l border-[#EEEEEE]/25 shadow-xl hidden md:flex">
                    <div>
                        <div className="mb-8">
                            <img src={girsimage} className="w-24 h-24 object-contain rounded-full border-4 border-[#EEEEEE] shadow-md shadow-[#2FA084]/30" alt="Main Logo" />
                        </div>
                        <h2 className="text-3xl font-extrabold text-[#EEEEEE] mb-8 tracking-wide drop-shadow-lg">Welcome Back</h2>
                    </div>
                    <div className="flex flex-col items-start text-xs text-[#EEEEEE] font-light gap-1 italic opacity-80">
                        <span>Empowering GlobeOmni Connections</span>
                        <span className="text-[#6FCF9799]">A project by Aadvik Tech Services</span>
                    </div>
                </div>

                {/* Right Content: Login Form, fixed width box */}
                <div className="flex items-center justify-center h-[480px] min-h-[400px] max-h-[550px] bg-[#EEEEEE]/10 backdrop-blur-xl rounded-2xl md:rounded-l-none md:rounded-r-2xl shadow-2xl px-6 py-8 w-[370px] max-w-[400px] border-t border-b border-r border-[#EEEEEE]/25 flex-col z-10">
                    {/* Only show logo on small screens */}
                    <div className="mb-8 md:hidden">
                        <img src={girsimage} className="w-24 h-24 mx-auto object-contain rounded-full border-4 border-[#EEEEEE] shadow-md shadow-[#2FA084]/30" alt="Main Logo" />
                    </div>
                    <h2 className="text-3xl font-extrabold text-[#EEEEEE] mb-8 text-center tracking-wide drop-shadow-lg md:hidden">Welcome Back</h2>
                    <LoginForm />
                    {/* Only show taglines on small screens */}
                    <div className="mt-8 flex flex-col items-center text-xs text-[#EEEEEE] font-light gap-1 italic opacity-80 md:hidden">
                        <span>Empowering GlobeOmni Connections</span>
                        <span className="text-[#6FCF9799]">A project by Aadvik Tech Services</span>
                    </div>
                </div>
            </div>

            {/* Aesthetic SVG Illustration (background, subtle, large, behind form) */}
            <div className="absolute inset-0 pointer-events-none z-0">
                <svg viewBox="0 0 520 480" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full opacity-50">
                    <defs>
                        <linearGradient id="paint1" x1="0" y1="0" x2="400" y2="400" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#2FA084" />
                            <stop offset="1" stopColor="#1F6F5F" />
                        </linearGradient>
                        <radialGradient id="paint2" cx="60%" cy="40%" r="60%" fx="70%" fy="45%" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#6FCF97" stopOpacity="0.85"/>
                            <stop offset="1" stopColor="#0000" stopOpacity="0"/>
                        </radialGradient>
                    </defs>
                    <ellipse cx="260" cy="240" rx="205" ry="180" fill="url(#paint1)" opacity="0.5"/>
                    <ellipse cx="420" cy="70" rx="80" ry="65" fill="url(#paint2)" opacity="0.4"/>
                    <circle cx="120" cy="350" r="60" fill="#EEEEEE" fillOpacity="0.06"/>
                    <path d="M120,280Q170,340 220,245Q260,190 320,230T410,320"
                        stroke="#EEEEEE" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.18"/>
                    <circle cx="120" cy="280" r="9" fill="#6FCF97" opacity="0.5"/>
                    <circle cx="220" cy="245" r="7" fill="#2FA084" opacity="0.45"/>
                    <circle cx="320" cy="230" r="7" fill="#1F6F5F" opacity="0.4"/>
                    <circle cx="410" cy="320" r="11" fill="#2FA084" opacity="0.40"/>
                    <ellipse cx="160" cy="110" rx="11" ry="7" fill="#EEEEEE" opacity="0.08"/>
                    <ellipse cx="460" cy="410" rx="16" ry="11" fill="#EEEEEE" opacity="0.09"/>
                    <text x="260" y="476" textAnchor="middle" fill="#EEEEEE" fontSize="20" fontWeight="bold" opacity="0.18">
                        GlobeOmni
                    </text>
                </svg>
            </div>
            {/* Subtle animated dots (bottom right) */}
            <div className="absolute bottom-8 right-8 space-x-1 flex animate-bounce-slow z-10">
                <span className="w-2 h-2 rounded-full bg-[#6FCF97] opacity-60 block"></span>
                <span className="w-2 h-2 rounded-full bg-[#2FA084] opacity-40 block"></span>
                <span className="w-2 h-2 rounded-full bg-[#EEEEEE] opacity-30 block"></span>
            </div>
        </div>
    )
}

export default Login
